import type { ReactNode } from 'react';
import { Card, CardContent, Box, Divider } from '@citibox-panel/ui';
import type {
  BoxMarks,
  BoxStatus,
  ConfirmLockSequenceInput,
  DeliveryDetail,
  LockCodes,
  LockerLog,
} from '@citibox-panel/api-client';
import { hasLockCodes } from '../lib/hasLockCodes';
import { BoxMarksBanner } from './BoxMarksBanner';
import { BoxStatusBanner } from './BoxStatusBanner';
import { DeliveryHero } from './DeliveryHero';
import { DeliveryLocation } from './DeliveryLocation';
import { DeliveryTimeline } from './DeliveryTimeline';
import { LockCodesSection } from './LockCodesSection';
import { LockerLogSection } from './LockerLogSection';

export interface DeliveryDetailCardProps {
  detail: DeliveryDetail;
  logOpen: boolean;
  onToggleLog: () => void;
  fetchLockerLog: (boxId: string) => Promise<LockerLog>;
  fetchBoxStatus: (boxId: string, deliveryId: string) => Promise<BoxStatus>;
  fetchBoxMarks: (boxId: string) => Promise<BoxMarks>;
  clearBoxMarks: (boxId: string) => Promise<void>;
  fetchLockCodes: (lockId: string) => Promise<LockCodes>;
  confirmLockSequence: (lockId: string, input: ConfirmLockSequenceInput) => Promise<void>;
  legacyBoxMaintenanceUrl?: (boxId: string) => string | null;
  // «Ampliar custodia», already wired by the layout. Passed straight through to the trazabilidad,
  // which is where it hangs — this card is a route on the way, not a participant.
  extendCustodyControl?: ReactNode;
  // «Notificar mismatch», wired by the layout. Unlike the control above, this card is where it is
  // PLACED: under the «buzón», full width, above the dirección and trazabilidad columns.
  addressMismatchAlert?: ReactNode;
}

// The «Entrega» information card, no CardContent padding (each region pads its own
// content so the hero's divider runs edge to edge): hero on top, then dirección/cerradura
// beside the trazabilidad, split by a vertical divider. flex 2 against the «Acciones»
// card's 1 — two thirds of the row. When a «buzón» is/was associated, a disclosure toggle
// under CERRADURA unfolds its activity log full-width below the two columns. Still no
// «Titular» / «Compartido».
export function DeliveryDetailCard({
  detail,
  logOpen,
  onToggleLog,
  fetchLockerLog,
  fetchBoxStatus,
  fetchBoxMarks,
  clearBoxMarks,
  fetchLockCodes,
  confirmLockSequence,
  legacyBoxMaintenanceUrl,
  extendCustodyControl,
  addressMismatchAlert,
}: DeliveryDetailCardProps) {
  // The log is read BY BOX id (contract update 2026-07-27), so the «buzón» itself — associated now
  // or historically — is what gates the toggle: a delivery with a lock but no box has no log to read.
  const box = detail.box;
  const canViewLog = box !== null;
  // `hasLockCodes` is the gate — a cerradura devices-service never took over has no sequences and
  // the read would be a guaranteed 404. The re-read of `id` is only what TypeScript needs to carry
  // a non-null key across that predicate. The cerradura hangs off the «buzón» (CBX-10954), so a
  // boxless delivery reaches the gate as null and closes it for free.
  const lock = box?.lock ?? null;
  const lockId = hasLockCodes(lock) ? (lock?.id ?? null) : null;
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, flex: 2, minWidth: 0 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <DeliveryHero
          detail={detail}
          {...(legacyBoxMaintenanceUrl ? { legacyBoxMaintenanceUrl } : {})}
        />
        {/* Reads as a subtitle of the hero's «Buzón {matrícula}» title, which is why it sits here and
            not below the columns. Mounting it only when there is a box is what enforces «no box, no
            request»; the hook's own gate is the second line of defence. */}
        {box !== null && (
          <BoxStatusBanner
            boxId={box.id}
            boxNumber={box.number}
            deliveryId={detail.id}
            locationId={detail.location.id}
            locationAddress={detail.location.address}
            fetchBoxStatus={fetchBoxStatus}
          />
        )}
        {/* Under the «buzón» and its status notice, with the same inset: the parcel landed in THIS
            «buzón», and whether that is the titular's building is what the warning questions. */}
        {addressMismatchAlert !== undefined && (
          <Box sx={{ px: 2, pb: 2 }}>{addressMismatchAlert}</Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, px: 2, pb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <DeliveryLocation
              detail={detail}
              canViewLog={canViewLog}
              logOpen={logOpen}
              onToggleLog={onToggleLog}
            />
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1 }}>
            <DeliveryTimeline
              detail={detail}
              {...(extendCustodyControl !== undefined ? { extendCustodyControl } : {})}
            />
          </Box>
        </Box>
        {/* The region wrapper is mounted whenever the delivery has a «buzón» — a stable id for the
            toggle's aria-controls; the section keeps its query alive across collapse and renders
            nothing (zero-height) until unfolded, so the Divider + padding live inside it. */}
        {box !== null && (
          <Box id="locker-log-region">
            {/* ONE hairline box around both: what the agent unfolds is a single surface — the
                cerradura's codes and the «buzón»'s history — and framing each of them separately
                read as boxes inside a box. The chrome hangs off `logOpen` rather than off a
                conditional mount, because both sections must stay mounted while folded to keep
                their queries; folded they render nothing, so an unstyled wrapper is zero-height.
                The radius is a step tighter than the card's, which is what reads as nested. */}
            <Box
              sx={
                logOpen
                  ? // No top margin: the columns above already close with their own `pb`, and
                    // adding one here would stack two gaps between the toggle and the box.
                    { mx: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }
                  : {}
              }
            >
              {/* The codes come first: they are what the agent acts on, the log below is the
                  evidence. Two independent reads about two different devices, so one failing
                  never takes the other down. `lock.id` is re-read rather than captured above:
                  `hasLockCodes` is the gate and TypeScript cannot carry its verdict across it. */}
              {/* The level comes first: it is what tells the agent HOW desynced the «buzón» is, and
                  that decision precedes reading a code out. Box-first like the log, so it shows even
                  on a cerradura devices-service never took over — unlike the carriles below. */}
              <BoxMarksBanner
                open={logOpen}
                boxId={box.id}
                fetchBoxMarks={fetchBoxMarks}
                clearBoxMarks={clearBoxMarks}
              />
              {lockId !== null && (
                <LockCodesSection
                  open={logOpen}
                  lockId={lockId}
                  deliveryId={detail.id}
                  fetchLockCodes={fetchLockCodes}
                  confirmLockSequence={confirmLockSequence}
                />
              )}
              <LockerLogSection open={logOpen} boxId={box.id} fetchLockerLog={fetchLockerLog} />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
